(ns electron.listener
  "System-component-like ns that defines listeners by event name to receive ipc
  messages from electron's main process"
  (:require [cljs-bean.core :as bean]
            [clojure.string :as string]
            [dommy.core :as dom]
            [electron.ipc :as ipc]
            [electron.locale :as electron-locale]
            [frontend.context.i18n :as i18n]
            [frontend.db.async :as db-async]
            [frontend.handler.notification :as notification]
            [frontend.handler.route :as route-handler]
            [frontend.handler.search :as search-handler]
            [frontend.handler.ui :as ui-handler]
            [frontend.handler.user :as user]
            [frontend.persist-db :as persist-db]
            [frontend.state :as state]
            [frontend.ui :as ui]
            [promesa.core :as p]))

(defn- safe-api-call
  "Force the callback result to be nil, otherwise, ipc calls could lead to
  window crash."
  [k f]
  (js/window.apis.on k (fn [data] (f data) nil)))

(defn ^:large-vars/cleanup-todo listen-to-electron!
  []
  (safe-api-call "graph-lifecycle"
                 (fn [data]
                   (let [{:keys [repo phase generation]} (bean/->clj data)]
                     (persist-db/<invalidate-remote-repo! repo phase generation))))

  (safe-api-call "notification"
                 (fn [data]
                   (let [{:keys [type payload i18n-key i18n-args]} (bean/->clj data)
                         type (keyword type)
                         i18n-key (when i18n-key (keyword i18n-key))
                         content (if i18n-key
                                   (apply i18n/t i18n-key i18n-args)
                                   (str payload))
                         comp [:div content]]
                     (notification/show! comp type false))))

  (safe-api-call "rebuildSearchIndice"
                 (fn [_data]
                   (prn "Rebuild search indices")
                   (search-handler/rebuild-indices!)))

  (safe-api-call "setGitUsernameAndEmail"
                 (fn []
                   (state/pub-event! [:modal/set-git-username-and-email])))

  (safe-api-call "setCurrentGraph"
                 (fn []
                   (when-let [graph (state/get-current-repo)]
                     (ipc/ipc :setCurrentGraph graph))))

  (safe-api-call "redirect"
                 (fn [data]
                   (let [{:keys [payload]} (bean/->clj data)
                         payload (update payload :to keyword)]
                     (route-handler/redirect! payload))))

  (safe-api-call "redirectWhenExists"
                 ;;  Redirect to the given page or block when the provided page or block exists.
                 ;;  Either :page-name or :block-id is required.
                 ;;  :page-name : the title of the page.
                 ;;  :block-id : uuid.
                 (fn [data]
                   (let [{:keys [page-name block-id]} (bean/->clj data)]
                     (cond
                       page-name
                       (p/let [page (db-async/<get-block (state/get-current-repo) page-name {:children? false})]
                         (when page
                           (route-handler/redirect-to-page! page-name {:block-id block-id})))

                       block-id
                       (p/let [block (db-async/<get-block (state/get-current-repo) block-id {:children? false})]
                         (if block
                           (route-handler/redirect-to-page! block-id)
                           (notification/show! (i18n/t :electron/block-not-exist block-id) :error false)))))))

  (safe-api-call "foundInPage"
                 (fn [data]
                   (let [data' (bean/->clj data)]
                     (state/set-state! [:ui/find-in-page :matches] data')
                     (dom/remove-style! (dom/by-id "search-in-page-input") :visibility)
                     (dom/set-text! (dom/by-id "search-in-page-placeholder") "")
                     (ui/focus-element "search-in-page-input"))))

  (safe-api-call "loginCallback"
                 (fn [code]
                   (user/login-callback code)))

  (safe-api-call "quickCapture"
                 (fn [args]
                   (state/pub-event! [:editor/quick-capture args])))

  (safe-api-call "invokeCommand"
                 (fn [args]
                   (state/pub-event! [:editor/invoke-command args])))

  (safe-api-call "openNewWindowOfGraph"
                 ;; Handle open new window in renderer, until the destination graph doesn't rely on setting local storage
                 ;; No db cache persisting ensured. Should be handled by the caller
                 (fn [repo]
                   (ui-handler/open-new-window-or-tab! repo)))

  (safe-api-call "invokeLogseqAPI"
                 (fn [^js data]
                   (let [sync-id (.-syncId data)
                         method  (.-method data)
                         ns-method (some-> method (string/split "@"))
                         ns' (first ns-method)
                         method' (last ns-method)
                         args    (.-args data)
                         ret-fn! #(ipc/invoke (str :electron.server/sync! sync-id) %)
                         app? (contains? #{"app" "editor" "db" "cli"} ns')
                         ^js sdk1 (aget js/window.logseq "api")
                         ^js sdk2 (aget js/window.logseq "sdk")]

                     (try
                       (println "invokeLogseqAPI:" method ", args:" args)
                       (let [^js methodTarget (if app? sdk1 (aget sdk2 ns'))]
                         (when-not methodTarget
                           (throw (js/Error. (str "MethodNotExist: " method))))
                         (-> (p/promise (apply js-invoke methodTarget method' args))
                             (p/then #(ret-fn! %))
                             (p/catch #(do
                                         (js/console.error "Unexpected error:" %)
                                         (ret-fn! {:error (.-message %)})))))
                       (catch :default e
                         (js/console.error "Unexpected error:" e)
                         (ret-fn! {:error (.-message e)}))))))

  (safe-api-call "syncAPIServerState"
                 (fn [^js data]
                   (state/set-state! :electron/server (bean/->clj data))))

  (safe-api-call "handbook"
                 (fn [^js data]
                   (when-let [k (and data (.-key data))]
                     (state/open-handbook-pane! k)))))

(defn listen!
  []
  (listen-to-electron!)
  (electron-locale/push-locale! (state/get-state :preferred-language)))
